(() => {
  const formatMoney = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format((Number(cents) || 0) / 100);

  const variantCache = new Map();

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    if (!variantCache.has(handle)) {
      variantCache.set(
        handle,
        fetch(`/products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return variantCache.get(handle);
  };

  class NativeConfigurator extends HTMLElement {
    connectedCallback() {
      this.data = window.NativeConfiguratorData;
      this.productId = this.dataset.productId;
      this.form = this.closest('product-info')?.querySelector('product-form form');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.sets = this.getSets();

      if (!this.data || !this.sets.length || !this.form) return;

      this.hidden = false;
      this.render();
      this.attachFormHandler();
      this.refreshAvailability();
    }

    getSets() {
      const setIds = this.data?.products?.[this.productId] || [];
      return setIds
        .map((setId) => this.data.sets.find((set) => set.id === setId))
        .filter(Boolean);
    }

    render() {
      const container = this.querySelector('[data-configurator-sets]');
      container.innerHTML = this.sets.map((set) => this.renderSet(set)).join('');
      this.querySelectorAll('select').forEach((select) => {
        select.addEventListener('change', () => this.updateSummary());
      });
      this.updateSummary();
    }

    renderSet(set) {
      const options = set.options.map((option) => this.renderOption(set, option)).join('');
      return `
        <section class="native-configurator__set" data-set-id="${set.id}">
          <h3 class="native-configurator__set-title">${this.escape(set.name || 'Konfiguration')}</h3>
          ${options}
        </section>
      `;
    }

    renderOption(set, option) {
      const inputId = `config-${set.id}-${option.id}`;
      const choices = option.values
        .map((value, index) => {
          const encoded = this.escape(JSON.stringify({ setId: set.id, optionId: option.id, index }));
          const price = value.productId === this.productId ? '' : this.priceLabel(value.price);
          return `<option value="${encoded}" data-handle="${this.escape(value.productHandle)}" data-variant-id="${this.escape(
            value.variantId
          )}">${this.escape(value.label)}${price}</option>`;
        })
        .join('');
      return `
        <div class="native-configurator__field">
          <label class="native-configurator__label" for="${inputId}">
            <span>${this.escape(option.label)}</span>
            ${option.required ? '<span class="native-configurator__required">Pflicht</span>' : ''}
          </label>
          <select id="${inputId}" class="native-configurator__select" data-required="${option.required}" data-label="${this.escape(
        option.label
      )}">
            <option value="">${this.escape(option.placeholder || '-- Bitte wählen --')}</option>
            ${choices}
          </select>
        </div>
      `;
    }

    priceLabel(price) {
      if (!price) return '';
      return ` (+${formatMoney(price)})`;
    }

    getSelected() {
      return [...this.querySelectorAll('select')]
        .map((select) => {
          if (!select.value) return null;
          const pointer = JSON.parse(select.value);
          const set = this.sets.find((entry) => entry.id === pointer.setId);
          const option = set?.options.find((entry) => entry.id === pointer.optionId);
          const value = option?.values[pointer.index];
          return value ? { set, option, value, select } : null;
        })
        .filter(Boolean);
    }

    validate() {
      let valid = true;
      this.querySelectorAll('.native-configurator__error').forEach((node) => node.remove());
      this.querySelectorAll('select').forEach((select) => {
        const missing = select.dataset.required === 'true' && !select.value;
        select.classList.toggle('is-invalid', missing);
        if (missing) valid = false;
      });

      if (!valid) {
        this.insertAdjacentHTML(
          'beforeend',
          '<p class="native-configurator__error">Bitte alle Pflichtfelder der Konfiguration auswählen.</p>'
        );
      }

      return valid;
    }

    updateSummary() {
      const selected = this.getSelected();
      const summary = this.querySelector('[data-configurator-summary]');
      if (!selected.length) {
        summary.hidden = true;
        summary.innerHTML = '';
        return;
      }

      const lines = selected
        .map(({ option, value }) => {
          const price = value.productId === this.productId ? '' : this.priceLabel(value.price).replace(' (', '').replace(')', '');
          return `<li><span>${this.escape(option.label)}: ${this.escape(value.label)}</span><span class="native-configurator__summary-price">${price}</span></li>`;
        })
        .join('');
      const addonTotal = selected
        .filter(({ value }) => value.productId !== this.productId)
        .reduce((sum, { value }) => sum + (Number(value.price) || 0), 0);

      summary.hidden = false;
      summary.innerHTML = `
        <p class="native-configurator__summary-title">Auswahl</p>
        <ul class="native-configurator__summary-list">${lines}</ul>
        <div class="native-configurator__total"><span>Aufpreis Bauteile</span><span>${formatMoney(addonTotal)}</span></div>
      `;
    }

    async refreshAvailability() {
      const options = [...this.querySelectorAll('option[data-handle]')];
      await Promise.all(
        options.map(async (option) => {
          const handle = option.dataset.handle;
          const variantId = Number(option.dataset.variantId);
          if (!handle || !variantId) return;
          const product = await fetchProduct(handle);
          const variant = product?.variants?.find((item) => Number(item.id) === variantId);
          if (variant && !variant.available) {
            option.disabled = true;
            option.textContent = `${option.textContent} - nicht verfügbar`;
          }
        })
      );
    }

    attachFormHandler() {
      if (this.form.dataset.nativeConfiguratorAttached === 'true') return;
      this.form.dataset.nativeConfiguratorAttached = 'true';
      this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
    }

    async onSubmit(event) {
      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      await this.addConfiguredItems();
    }

    async addConfiguredItems() {
      const submitButton = this.form.querySelector('[type="submit"]');
      const spinner = submitButton?.querySelector('.loading-overlay__spinner');
      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      const formData = new FormData(this.form);
      const quantity = Number(formData.get('quantity') || 1);
      const selected = this.getSelected();
      const variantInput = this.form.querySelector('[name="id"]');
      const fallbackVariant = formData.get('id') || variantInput?.value;
      const mainVariant = selected.find(({ value }) => value.productId === this.productId)?.value.variantId || fallbackVariant;
      const properties = {};

      if (!mainVariant) {
        this.showFormError('Die Hauptvariante konnte nicht ermittelt werden. Bitte Seite neu laden und erneut versuchen.');
        return;
      }

      selected.forEach(({ option, value }) => {
        properties[option.label] = value.label;
      });

      const items = [
        {
          id: Number(mainVariant),
          quantity,
          properties,
        },
      ];

      selected.forEach(({ value }) => {
        const isCurrentProductVariant = value.productId === this.productId;
        const isPlaceholderComponent = value.productHandle === 'angeliefertes-bauteil-wieder-einbauen';
        const shouldAddComponent = !isCurrentProductVariant && !isPlaceholderComponent && value.variantId;
        if (shouldAddComponent) {
          items.push({
            id: Number(value.variantId),
            quantity,
            properties: {
              _Konfiguration: this.dataset.productTitle,
            },
          });
        }
      });

      const body = { items };
      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        }).then((item) => item.json());

        if (response.status) {
          this.showFormError(response.description || response.message || 'Die Konfiguration konnte nicht hinzugefügt werden.');
          return;
        }

        if (this.cart) {
          if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.cart.renderContents(response);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        this.showFormError('Die Konfiguration konnte nicht hinzugefügt werden.');
      } finally {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    }

    showFormError(message) {
      const wrapper = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const text = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !text) return;
      text.textContent = message;
      wrapper.hidden = false;
    }

    escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('native-configurator')) {
    customElements.define('native-configurator', NativeConfigurator);
  }
})();
